import { type FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useRequestUtils } from "@/components/RequestData/Provider/useRequestUtils";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { SheetStep } from "@/ui/Popups/SheetStep";
import { SheetStepButton } from "@/ui/Popups/SheetStepButton";
import type { NurseInShift, OpenShiftRequest, ReplaceShiftRequest, RequestResponsesStatus } from "@/utils/models";
import { GAPS_DAY_LABEL_FORMAT, joinLabelParts } from "../gapsLabels";
import { staffingDayKey, staffingDayOfRequest } from "../gapsRequests";
import { useGapsApplicants } from "../openShifts/useGapsApplicants";
import { useGapsRequestLabels } from "../openShifts/useGapsRequestLabels";
import { GapsAssignUnitReason, type GapsSheetIntentOf, GapsSheetKind } from "../useGapsSheet";
import { useOpenOnGapsDay } from "../useOpenOnGapsDay";
import { GapsApplicantFacts } from "./GapsApplicantFacts";
import { GapsApproveNotes } from "./GapsApproveNotes";
import { GapsApproveWarningDialog } from "./GapsApproveWarningDialog";
import { GapsJobBadge } from "./GapsJobBadge";
import { useApprovalOvertime } from "./useApprovalOvertime";
import { useGapsApprove } from "./useGapsApprove";

/** The approve confirm's test-id stem: `-sheet` on the drawer, `-confirm` / `-cancel` on its buttons. */
export const APPROVE_SHEET = "gaps-approve-confirm";
const SHEET = APPROVE_SHEET;

type StepProps = {
	/** The intent this confirm is up for — what the approval hands on from, and only while it is still up. */
	opening: GapsSheetIntentOf<GapsSheetKind.ApproveConfirm>;
	request: OpenShiftRequest | ReplaceShiftRequest;
	response: RequestResponsesStatus;
	isRequestLive: boolean;
	/** See `useGapsApprove`'s `onHoldChange`: the sheet holds the request still while the approval runs. */
	onHoldChange: (isHeld: boolean) => void;
	onClose: () => void;
};

/**
 * The confirm itself: who, which shift, what approving does to everyone else who asked, and the facts the approval
 * rests on (`GapsApplicantFacts`). Confirming runs `useGapsApprove`; on success it hands on to the home's assign-unit
 * sheet on the request's day, or — for a swap, which creates nothing to place — to that day alone.
 */
export const GapsApproveConfirmStep: FC<StepProps> = ({ opening, request, response, isRequestLive, onHoldChange, onClose }) => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const { isPartialShift } = useRequestUtils();
	const { sectionsOf, headingOf } = useGapsRequestLabels();
	const { handOnToDay, leaveToDay } = useOpenOnGapsDay();
	// The person's marks (partial, agency, a double) without the list's overtime, which is for the POSTED hours.
	const { applicants } = useGapsApplicants(request, false);
	const applicant = applicants.find((entry) => entry.nurseId === response.nurse_id);

	// The staffing day the request is drawn on — the day the home lists it under, and the one the approval lands on.
	const day = staffingDayOfRequest(request, {
		dayStart: chosenDepartment.dayStart,
		scheduled_shift_delta_minutes: chosenDepartment.scheduled_shift_delta_minutes,
	});
	const dayISO = staffingDayKey(day);

	// Both hand-ons act only while this confirm is still the sheet up: an approval that finished after the user closed it,
	// or moved to another sheet, leaves the person on the shift with no unit — the "Assign later" state — and moves nothing.
	const handleApproved = useCallback(
		(created: NurseInShift | null): void => {
			if (created === null) {
				// Nothing here to place (a swap, or a week the phone has not loaded): on to the day, as the classic approve goes.
				leaveToDay(opening, dayISO);
				return;
			}
			// Replaces this sheet with the assign-unit sheet (one sheet at a time) and moves to the request's day.
			handOnToDay(opening, { kind: GapsSheetKind.AssignUnit, dayISO, shiftId: created.id, reason: GapsAssignUnitReason.Approve });
		},
		[dayISO, opening, leaveToDay, handOnToDay],
	);

	const { approve, cancelWarnings, warnings, hours, isBusy } = useGapsApprove({ request, response, onApproved: handleApproved, onHoldChange });
	const overtime = useApprovalOvertime(request, response.nurse_id, hours);

	const name = getNurseFormattedName(response.nurse_id);
	const [section] = sectionsOf(request);
	const subtitle = joinLabelParts([headingOf(request), day.toFormat(GAPS_DAY_LABEL_FORMAT), request.location]);

	return (
		<>
			<SheetStep
				stepKey={SHEET}
				dataTestId={SHEET}
				leading={section === undefined ? undefined : <GapsJobBadge group={section} dataTestId={`${SHEET}-job-badge`} />}
				title={t("gaps.approve.title", { name })}
				subtitle={subtitle}
				footer={
					<>
						<SheetStepButton
							tone="dismiss"
							onClick={() => {
								track("gaps_approve_dismissed", { department_id: chosenDepartment.department_id, request_id: request.id, nurse_id: response.nurse_id });
								onClose();
							}}
							disabled={isBusy}
							dataTestId={`${SHEET}-cancel`}
						>
							{t("cancel")}
						</SheetStepButton>
						<SheetStepButton
							tone="confirm"
							onClick={() => void approve()}
							// The request closed or the person withdrew while the sheet was up: there is nothing left to approve.
							disabled={!isRequestLive && !isBusy}
							loading={isBusy}
							dataTestId={`${SHEET}-confirm`}
						>
							{t("gaps.approve.confirm")}
						</SheetStepButton>
					</>
				}
			>
				<GapsApplicantFacts
					request={request}
					applicant={applicant}
					// The approve write's own test for a partial pickup — it decides the hours AND that no bonus is written.
					isPartial={isPartialShift(request, response.nurse_id)}
					hours={hours}
					overtimeHours={overtime.hours}
					isOvertimeLoading={overtime.isLoading}
					dataTestId={`${SHEET}-facts`}
				/>
				<GapsApproveNotes request={request} isRequestLive={isRequestLive} isBusy={isBusy} testIdPrefix={SHEET} />
			</SheetStep>
			<GapsApproveWarningDialog warnings={warnings} onCancel={cancelWarnings} />
		</>
	);
};
